/**
 * The banner region: one strip under the summary, red over amber over blue.
 *
 * Density is the whole design here. Two banners are shown; a third and further
 * collapse into one row that expands on click, and on a phone only the highest
 * severity is shown with a count — an app that shows six advisories at once has
 * taught the pilot to scroll past all of them.
 *
 * Announcement: red is assertive, amber and blue are polite, and each region is
 * keyed by the set of codes in it, so a re-render (a wind arriving, a keystroke)
 * does not read the same sentence out again.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useT, warnText } from '../i18n';
import { useNarrow } from '../media';
import { usePlanRev, usePlanStore } from '../planContext';
import { bannersFromStore, sortBanners, usePublishedBanners, type Banner, type Severity } from './severity';
import { IconInfo, IconWarning } from './icons';
import type { TabKey } from './tabs';

const icon = (severity: Severity): ReactNode =>
  severity === 'info' ? <IconInfo /> : <IconWarning />;

/** Everything the plan and the panes have to say, sorted and de-duplicated. */
export function useFlightBanners(extra: readonly Banner[] = []): Banner[] {
  const store = usePlanStore();
  const rev = usePlanRev();
  const published = usePublishedBanners();
  return useMemo(() => {
    const seen = new Set<string>();
    const all: Banner[] = [];
    for (const b of [...bannersFromStore(store), ...published, ...extra]) {
      const id = b.code + (b.params?.['leg'] ?? '');
      if (seen.has(id)) continue;
      seen.add(id);
      all.push(b);
    }
    return sortBanners(all);
    // `rev` is the store's change signal; `store` itself never changes identity
  }, [store, rev, published, extra]);
}

function BannerRow({
  banner,
  action,
  onGoto,
}: {
  banner: Banner;
  action?: ReactNode;
  onGoto?: ((tab: TabKey) => void) | undefined;
}) {
  const t = useT();
  const text = banner.key
    ? t(banner.key, banner.params)
    : warnText(t, {
        code: banner.code,
        ...(banner.params ? { params: banner.params } : {}),
        ...(banner.fallback !== undefined ? { fallback: banner.fallback } : {}),
      });
  return (
    <div className={'ae-banner ' + banner.severity}>
      {icon(banner.severity)}
      <span className="ae-banner-text">{text}</span>
      {action}
      {banner.tab && onGoto ? (
        <button type="button" className="ae-banner-link" onClick={() => onGoto(banner.tab as TabKey)}>
          {t('insp.' + banner.tab)}
        </button>
      ) : null}
    </div>
  );
}

export function Banners({
  banners,
  actions,
  onGoto,
}: {
  banners: readonly Banner[];
  /** per-code controls that belong inside the banner (the sync conflict's two) */
  actions?: Record<string, ReactNode>;
  onGoto?: ((tab: TabKey) => void) | undefined;
}) {
  const t = useT();
  const narrow = useNarrow();
  const [expanded, setExpanded] = useState(false);

  const shown = useMemo(() => {
    if (banners.length === 0) return [];
    if (expanded) return banners;
    if (narrow) {
      const top = banners[0];
      return top ? [top] : [];
    }
    return banners.slice(0, 2);
  }, [banners, narrow, expanded]);

  if (banners.length === 0) return null;
  const hidden = banners.length - shown.length;

  const rows = shown.map(b => (
    <BannerRow key={b.code + (b.params?.['leg'] ?? '')} banner={b} action={actions?.[b.code]} onGoto={onGoto} />
  ));

  const alerts = rows.filter((_, i) => shown[i]?.severity === 'warning');
  const notices = rows.filter((_, i) => shown[i]?.severity !== 'warning');

  return (
    <div className="ae-banners">
      <div role="alert" aria-live="assertive">
        {alerts}
      </div>
      <div role="status" aria-live="polite">
        {notices}
      </div>
      {hidden > 0 ? (
        <button type="button" className="ae-banner-more" onClick={() => setExpanded(true)}>
          {t('bn.more', { n: hidden })}
        </button>
      ) : null}
    </div>
  );
}
